import { Component, ComponentRef, Injector, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { IManagableObject } from "../../IManagableObject";
import { KeyViewModel } from "../Model/KeyViewModel";
import { DialogBaseComponent } from "../../base-admin-panel/dialog-base/dialog-base.component";
import { KeysService } from "../../../../services/keys.service";
import { ObjectsManagementDialogComponent } from "../../objects-management-dialog/objects-management-dialog.component";
import { DialogService } from "../../../../services/dialog.service";
import { DriversService } from "../../../../services/drivers.service";
import { ObjectsService } from "../../../../services/objects.service";
import { ContainersService } from "../../../../services/containers.service";
import { ContainerViewModel } from "../../ContainerViewModel";
import { ContainerAcl } from "app/models/ContainerAcl";

@Component({
  selector: 'app-keys-dialog-base',
  templateUrl: './keys-dialog-base.component.html',
  styleUrls: ['./keys-dialog-base.component.css'],
  standalone: false
})
export class KeysDialogBaseComponent extends DialogBaseComponent<KeyViewModel> implements OnInit {
  @ViewChild("objectsListDialog", { read: ViewContainerRef, static: true }) objectsListContainer;
  private objectsListDialog: ComponentRef<any>;

  @Input()
  public drivers: IManagableObject[] = [];

  @Input()
  public trackers: IManagableObject[] = [];

  public groups: IManagableObject[] = [];
  public containers: ContainerViewModel[] = [];
  public creatableContainers: ContainerViewModel[] = [];
  public currentUser: import("../../../../services/users.service").UserViewModel;
  public openedTabName: string = "main";

  private driversService: DriversService;
  private objectsService: ObjectsService;
  private containersService: ContainersService;
  protected keysService: KeysService;
  private dialogService: DialogService;

  constructor(protected injector: Injector) {
    super(injector);
    this.keysService = injector.get(KeysService);
    this.dialogService = injector.get(DialogService);
    this.driversService = injector.get(DriversService);
    this.objectsService = injector.get(ObjectsService);
    this.containersService = injector.get(ContainersService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.currentUser = this.usersService.getCurrentUser();

    this.driversService.GetAllDrivers()
      .subscribe(drivers => {
        this.drivers = drivers;
      });

    this.objectsService.GetObjects()
      .subscribe(trackers => {
        this.trackers = trackers;
      });

    this.keysService.GetGroups()
      .subscribe(groups => {
        this.groups = groups;
      });

    this.loadContainers();
  }

  public override open(model: any) {
    this.model = model ?? new KeyViewModel();
    this.loadContainers();
    super.open(this.model);
  }

  public openTab(name: string) {
    this.openedTabName = name;
  }

  public isSelected(name: string) {
    return name == this.openedTabName;
  }

  public get isEditMode(): boolean {
    return (this.model?.Id ?? 0) > 0;
  }

  public getSelectedContainerName(): string {
    const containerId = this.model?.ContainerId;
    if (!containerId) return "";
    return this.containers.find(x => x.Id === containerId)?.Name ?? "";
  }

  public show(id: number) {
    this.model = new KeyViewModel();
    this.keysService.GetKey(id)
      .subscribe(key => {
        this.model = key;
        if (this.model)
          this.open(this.model);
      });
  }

  public clearDriver() {
    this.model.DriverId = undefined;
    this.model.DriverName = "";
  }

  public selectDriver() {
    this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
    this.objectsListDialog.instance.isSingleItem = true;
    this.objectsListDialog.instance.onOpen.subscribe(() => {
      this.objectsListDialog.instance.objects = this.drivers;
    });
    this.objectsListDialog.instance.onSubmit.subscribe(() => {
      this.objectsListDialog.instance.objects.forEach(driver => {
        if (driver && driver.IsChecked) {
          this.model.DriverId = driver.Id;
          this.model.DriverName = driver.Name;
        }
      });
      this.objectsListDialog.instance.close();
    });
    this.objectsListDialog.instance.onClose.subscribe(() => {
    });
    this.objectsListDialog.instance.open();
  }

  private ensureContainerSelected() {
    if (!this.model) return;

    const availableContainers = this.isEditMode ? this.containers : this.creatableContainers;
    if (availableContainers.length === 0) {
      if (!this.isEditMode)
        this.model.ContainerId = undefined;
      return;
    }

    if (this.model.ContainerId && availableContainers.some(x => x.Id === this.model.ContainerId))
      return;
    if (!this.isEditMode)
      this.model.ContainerId = availableContainers[0].Id;
  }

  private loadContainers() {
    const companyId = this.usersService.getCurrentUser()?.CompanyId;
    if (!companyId) {
      this.containers = [];
      this.creatableContainers = [];
      if (this.model && !this.isEditMode)
        this.model.ContainerId = undefined;
      return;
    }

    this.containersService.GetContainers()
      .subscribe(items => {
        this.containers = [...(items ?? [])]
          .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
        this.creatableContainers = this.getCreatableContainers(this.containers);
        this.ensureContainerSelected();
      });
  }

  private getCreatableContainers(containers: ContainerViewModel[]): ContainerViewModel[] {
    return containers.filter(container =>
      (this.getContainerAcl(container) & ContainerAcl.CreateDriverKey) === ContainerAcl.CreateDriverKey);
  }

  private getContainerAcl(container: ContainerViewModel): number {
    if (!this.currentUser)
      return 0;

    if (this.currentUser.CanDo(this.AccessRights.EDITUSERS) || this.currentUser.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return ContainerAcl.CreateDriverKey | ContainerAcl.EditDriverKey | ContainerAcl.DeleteDriverKey;

    const userId = this.currentUser.Id;
    const aclFromUsers = container.Users?.find(link => link.UserId === userId)?.ACL;
    const fallbackAcl = (this.currentUser.ContainerLinks ?? []).find(link => link.ContainerId === container.Id)?.ACL;
    return aclFromUsers ?? fallbackAcl ?? 0;
  }
}
